/**
 * Chapter 7, the insight and the idea: what the research says (13), the
 * method (47), the insight (04), the idea in one line (48), the idea in three
 * posts (20).
 */
import type { FindingChapter } from "@/types/strategyDocument.type";
import {
  Eyebrow, Foot, Headline, Key, Picture, Slide, SourceLine, V,
  accountName, dayLong, pillarColour, sentence, slideText,
  type TemplateContext,
} from "./kit";

/* ── 13 What the research says ────────────────────────────────────────── */

const FINDING_KEYS: Record<FindingChapter, string> = {
  industry: "The industry",
  audience: "The audience",
  brand: "The brand",
  competitors: "The competitors",
  culture: "The conversation",
};

export function FindingsSlide({ ctx }: { ctx: TemplateContext }) {
  const findings = ctx.doc.insight!.findings;
  const t = slideText(ctx);
  return (
    <Slide ink>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback={"What the research\n*says.*"} size={54} lineHeight={60} />
        <span className="sv-push" />
        <div className="sv-findings">
          {findings.slice(0, 6).map((f) => (
            <div key={f.id} className="sv-finding">
              <p className="sv-finding-key">{FINDING_KEYS[f.chapter]}</p>
              <p className="sv-finding-text">
                {f.kind === "open" ? <V className="sv-open">{f.source?.what_would_settle_it ?? f.headline}</V> : <><V>{sentence(f.headline)}</V>{f.so_what ? <> <V>{sentence(f.so_what)}</V></> : null}</>}
              </p>
            </div>
          ))}
        </div>
        <Foot ctx={ctx} />
      </div>
    </Slide>
  );
}

/* ── 47 The method ────────────────────────────────────────────────────── */

export function MethodSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const method = doc.insight!.method!;
  const t = slideText(ctx);
  const swap = method.logo_swap;
  const rival = swap?.rival_id ? accountName(doc, ctx.brand, swap.rival_id) : null;
  const convention = method.convention;
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback="Swap the name. *Nothing breaks.*" />
        <div className="sv-method">
          {swap ? (
            <div className="sv-method-test">
              <div className="sv-method-posts">
                {[ctx.brand, rival].filter((n): n is string => !!n).map((name, i) => (
                  <div key={name} className="sv-method-post">
                    <div className="sv-method-frame">
                      <Picture ctx={ctx} slot="chapter:method" label="The logo-swap post" url={swap.post?.picture_url} className="sv-fill-pic" />
                      <V className="sv-method-chip" style={{ background: i ? "var(--red)" : "var(--blue)" }}>{name}</V>
                    </div>
                    {swap.caption ? <V as="p" className="sv-method-caption">{swap.caption}</V> : null}
                  </div>
                ))}
              </div>
              {swap.reading ? <V as="p" className="sv-method-reading">{swap.reading}</V> : null}
            </div>
          ) : null}
          <div className="sv-method-argument">
            {convention ? (
              <div className="sv-method-block">
                <Key>The convention{convention.read_at ? <>, read <V>{dayLong(convention.read_at)}</V></> : null}</Key>
                <V as="p">{convention.text}</V>
              </div>
            ) : null}
            {method.belief ? (
              <div className="sv-method-block">
                <Key>The belief underneath</Key>
                <V as="p">{method.belief}</V>
              </div>
            ) : null}
            {method.broke_elsewhere ? (
              <div className="sv-method-block">
                <Key>What broke it elsewhere</Key>
                <V as="p">{method.broke_elsewhere.text}</V>
              </div>
            ) : null}
            {method.distinction ? (
              <div className="sv-method-distinction">
                <p className="sv-key">The distinction</p>
                <p className="sv-method-line">
                  <span>The category counts</span><V as="b">{method.distinction.category_counts}</V>
                  <span>The audience counts</span><V as="b">{method.distinction.audience_counts}</V>
                </p>
              </div>
            ) : null}
          </div>
        </div>
        <span className="sv-push" />
        <Foot ctx={ctx} extra=" · The method" />
      </div>
    </Slide>
  );
}

/* ── 04 The insight ───────────────────────────────────────────────────── */

export function InsightSlide({ ctx }: { ctx: TemplateContext }) {
  const insight = ctx.doc.insight!;
  const one = insight.insights[0];
  const t = slideText(ctx);
  const why = insight.method?.why_unquestioned;
  return (
    <Slide ink className="sv-insight">
      <div className="sv-col sv-insight-copy">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline ?? one.text} size={66} lineHeight={74} />
        <span className="sv-push" />
        <div className="sv-plays">
          {one.proofs.slice(0, 3).map((p) => (
            <div key={p.label} className="sv-play">
              <V as="p" className="sv-play-head">{p.label}</V>
              <V as="p" className="sv-play-text">{p.text}</V>
            </div>
          ))}
        </div>
        {why ? (
          <div className="sv-play sv-play-wide">
            <p className="sv-play-head sv-play-head-plain">Why nobody questioned it</p>
            <V as="p" className="sv-play-text">{why}</V>
          </div>
        ) : null}
        {one.source?.kind === "open" ? <SourceLine source={one.source} /> : null}
        <Foot ctx={ctx} label="The insight" />
      </div>
      <Picture ctx={ctx} slot="chapter:insight" label="The insight’s picture" className="sv-insight-pic" />
    </Slide>
  );
}

/* ── 48 The idea, in one line ─────────────────────────────────────────── */

export function IdeaSlide({ ctx }: { ctx: TemplateContext }) {
  const insight = ctx.doc.insight!;
  const idea = insight.idea!;
  const t = slideText(ctx);
  const check = idea.checked_against;
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback="The idea, *in one line.*" />
        <div className="sv-idea">
          <V as="p" className="sv-idea-line">{idea.line}</V>
          {check ? (
            <p className="sv-idea-check">
              Checked against what <V>{ctx.brand}</V> says about itself, “<V>{check.line}</V>”.{check.verdict ? <> <V>{check.verdict}</V></> : null}
            </p>
          ) : null}
        </div>
        {insight.known_for.length || insight.not_for_us.length ? (
          <div className="sv-idea-blocks">
            {insight.known_for.slice(0, 2).map((k) => (
              <div key={k.id} className="sv-idea-block">
                <Key>Known for</Key>
                <V as="p">{k.text}</V>
              </div>
            ))}
            {insight.not_for_us.length ? (
              <div className="sv-idea-block">
                <Key className="sv-key-red">We will not</Key>
                <V as="p">{insight.not_for_us.map(sentence).join(" ")}</V>
              </div>
            ) : null}
          </div>
        ) : null}
        <Foot ctx={ctx} label="The idea" />
      </div>
    </Slide>
  );
}

/* ── 20 The idea, in three posts ──────────────────────────────────────── */

export function IdeaPostsSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const idea = doc.insight!.idea!;
  const t = slideText(ctx);
  return (
    <Slide ink>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback="The idea, *in three posts.*" size={54} lineHeight={60} />
        <div className="sv-ideas">
          {idea.example_posts.slice(0, 3).map((p, i) => {
            const pillar = doc.content?.pillars.find((x) => x.id === p.pillar_id);
            const when = [p.format, p.day].filter(Boolean).join(", ");
            return (
              <div key={i} className="sv-idea-card">
                <div className="sv-idea-well">
                  <Picture ctx={ctx} slot={`idea-post:${i + 1}`} label={`Example post ${i + 1}`} className="sv-idea-pic" />
                </div>
                {pillar || when ? (
                  <p className="sv-idea-meta">
                    {pillar ? <i style={{ background: pillarColour(doc, pillar.id, true) }} /> : null}
                    <span>{pillar ? <V>{pillar.name}</V> : null}{pillar && when ? " · " : null}{when ? <V>{when}</V> : null}</span>
                  </p>
                ) : null}
                <V as="p" className="sv-idea-hook">{p.hook}</V>
                {p.note ? <V as="p" className="sv-idea-note">{p.note}</V> : null}
              </div>
            );
          })}
        </div>
        <span className="sv-push" />
        {t.reading ? <V as="p" className="sv-idea-pattern">{t.reading}</V> : null}
        <Foot ctx={ctx} flag={<SourceLine className="sv-flag-line">Example posts</SourceLine>} />
      </div>
    </Slide>
  );
}
